// Brain cards (contracts/learn.md §2): one short, sourced card a day, the same for every player on a date. The
// calendar shows which card each date resolves to (scheduled for it, else the evergreen rotation, else none);
// the library lists every card. Editors create, edit and delete; every role reads.

import { useState } from "react";
import {
  type AdminBrainCard,
  BRAIN_CATEGORIES,
  BRAIN_CATEGORY_HINT,
  BRAIN_CATEGORY_LABEL,
  BRAIN_CATEGORY_TONE,
  BRAIN_LIMITS,
  type BrainCalendarDay,
  type BrainCardDraft,
  type BrainCategory,
  type BrainContent,
  type BrainStatus,
  useBrainCards,
  useCreateBrainCard,
  useDeleteBrainCard,
  useUpdateBrainCard,
} from "../api/learn";
import { type ImageRef, type Locale, RTL_LOCALES } from "../api/messaging";
import { useAuth, useCan } from "../auth/AuthContext";
import { tableStyles } from "../components/table";
import { Button, Card, Chip, ConfirmDialog, Dialog, EmptyState, ErrorState, Field, InlineError, Loading, PageHeader, Segmented, TextInput } from "../components/ui";
import { formatShortDate, todayUtc, weekdayMon0 } from "../lib/dates";
import { fmtInt } from "../lib/format";
import { resolveEntry, toLocalized } from "../lib/messaging";
import br from "./brain.module.css";
import { ClaimsNote } from "./learn/components";
import { ContentEditor, type ContentState, type FieldSpec, ImageField, SwitchRow, textState } from "./messaging/components";
import m from "./messaging/messaging.module.css";
import styles from "./pages.module.css";

const TEXT_FIELDS = ["title", "text", "more", "source"] as const;
type TextKey = (typeof TEXT_FIELDS)[number];
const TEXT_SPECS: readonly FieldSpec<TextKey>[] = [
  { key: "title", label: "Title", limit: BRAIN_LIMITS.title, placeholder: "e.g. Practice is specific", hint: "the push text" },
  { key: "text", label: "Text", limit: BRAIN_LIMITS.text, rows: 3, placeholder: "The card itself: one idea, in plain words." },
  { key: "more", label: "Why", limit: BRAIN_LIMITS.more, rows: 5, placeholder: "Optional: shown when the card is opened.", hint: "optional" },
  { key: "source", label: "Source", limit: BRAIN_LIMITS.source, placeholder: "e.g. Ericsson et al., 1993, Psychological Review" },
];
const SOURCE_RULE = 'A factual card needs a source before it can be ready (only "habit" cards may go without).';
/** Recommended card image (wide, like announcement images). */
const IMAGE = { width: 1200, height: 675 };

type Editing = { card: AdminBrainCard } | { card: null; date: string | null };

export function BrainPage() {
  const canEdit = useCan("brain.edit");
  const { admin } = useAuth();
  const data = useBrainCards();
  const [editing, setEditing] = useState<Editing | null>(null);
  const items = data.data?.items ?? [];
  const byId = new Map(items.map((c) => [c.id, c]));

  const openDay = (day: BrainCalendarDay) => {
    const card = day.cardId ? byId.get(day.cardId) : undefined;
    if (card && !day.evergreen) setEditing({ card });
    else if (canEdit) setEditing({ card: null, date: day.date });
    else if (card) setEditing({ card });
  };

  return (
    <>
      <PageHeader
        title="Brain cards"
        meta={
          canEdit ? (
            <span>One short, sourced card a day about how the brain and reasoning work: on Home, in the widget and as an opt-in push at each player's time.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · editing brain cards needs the editor role (you are {admin?.role})
            </span>
          )
        }
        actions={
          canEdit ? (
            <Button variant="primary" onClick={() => setEditing({ card: null, date: null })} data-testid="new-card">
              New card
            </Button>
          ) : null
        }
      />
      {data.isPending ? (
        <Card>
          <Loading rows={8} />
        </Card>
      ) : data.isError ? (
        <ErrorState error={data.error} onRetry={() => void data.refetch()} />
      ) : (
        <div className={br.stack}>
          <Card
            title="Calendar"
            actions={
              <span className={br.legend}>
                <span data-kind="scheduled">scheduled</span>
                <span data-kind="evergreen">evergreen</span>
                <span data-kind="empty">no card</span>
              </span>
            }
          >
            <Calendar days={data.data.calendar} cards={byId} onOpen={openDay} canEdit={canEdit} />
          </Card>
          <Card title={`Library · ${items.length}`} padded={false}>
            <Library items={items} onOpen={(card) => setEditing({ card })} />
          </Card>
        </div>
      )}
      {editing ? (
        <BrainDialog key={editing.card ? editing.card.id : `new-${editing.date ?? ""}`} editing={editing} cards={items} canEdit={canEdit} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- calendar

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Calendar({ days, cards, onOpen, canEdit }: { days: BrainCalendarDay[]; cards: Map<string, AdminBrainCard>; onOpen: (d: BrainCalendarDay) => void; canEdit: boolean }) {
  const today = todayUtc();
  const lead = days.length ? weekdayMon0(days[0]!.date) : 0;
  const empty = days.filter((d) => !d.cardId && d.date >= today).length;
  return (
    <>
      {empty ? (
        <p className={br.gap} data-testid="calendar-gaps">
          {empty} {empty === 1 ? "day" : "days"} ahead {empty === 1 ? "has" : "have"} no card: schedule one, or mark ready cards as evergreen.
        </p>
      ) : null}
      <div className={br.calendar} role="grid" aria-label="Brain card calendar (UTC dates)" data-testid="brain-calendar">
        {WEEKDAYS.map((w) => (
          <span key={w} className={br.weekday} role="columnheader">
            {w}
          </span>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <span key={`pad-${i}`} aria-hidden />
        ))}
        {days.map((d) => {
          const card = d.cardId ? cards.get(d.cardId) : undefined;
          const kind = !d.cardId ? "empty" : d.evergreen ? "evergreen" : "scheduled";
          const clickable = Boolean(card) || canEdit;
          return (
            <button
              key={d.date}
              type="button"
              role="gridcell"
              className={br.day}
              data-kind={kind}
              data-today={d.date === today || undefined}
              data-past={d.date < today || undefined}
              data-date={d.date}
              disabled={!clickable}
              onClick={() => onOpen(d)}
              title={card ? `${kind === "evergreen" ? "Evergreen rotation" : "Scheduled"}: ${card.content.en?.title ?? ""}` : canEdit ? "No card: click to write one for this day" : "No card"}
              data-testid="calendar-day"
            >
              <span className={br.dayHead}>
                <span>{formatShortDate(d.date)}</span>
                {d.date === today ? <span className={br.todayTag}>today</span> : null}
              </span>
              <span className={br.dayTitle}>{card ? card.content.en?.title : "No card"}</span>
              <span className={br.dayKind}>{kind === "empty" ? (canEdit ? "+ write one" : "—") : kind}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- library

function Library({ items, onOpen }: { items: AdminBrainCard[]; onOpen: (c: AdminBrainCard) => void }) {
  if (items.length === 0) return <EmptyState title="No brain cards yet">Write the first one with “New card”.</EmptyState>;
  return (
    <div className={tableStyles.wrap}>
      <table className={tableStyles.table}>
        <thead>
          <tr>
            <th>Card</th>
            <th>Category</th>
            <th>Status</th>
            <th>Date</th>
            <th>Source</th>
            <th data-align="right">Opens</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => {
            const en = c.content.en;
            const needsSource = c.category !== "habit" && !en?.source;
            return (
              <tr key={c.id} data-clickable data-testid="card-row" onClick={(e) => !(e.target as HTMLElement).closest("button") && onOpen(c)}>
                <td>
                  <div className={br.cell}>
                    {c.image ? <img src={c.image.url} alt="" className={br.thumb} /> : null}
                    <div className={br.cellText}>
                      <button type="button" className={br.rowTitle} onClick={() => onOpen(c)}>
                        {en?.title ?? "Untitled"}
                      </button>
                      <span>{en?.text}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <Chip tone={BRAIN_CATEGORY_TONE[c.category]}>{BRAIN_CATEGORY_LABEL[c.category]}</Chip>
                </td>
                <td>
                  <Chip tone={c.status === "ready" ? "success" : "neutral"}>{c.status}</Chip>
                </td>
                <td className={br.when}>
                  {c.date ? <span className="mono">{c.date}</span> : null}
                  {c.evergreen ? <span className={br.evergreen}>evergreen</span> : null}
                  {!c.date && !c.evergreen ? <span className={styles.faint}>unscheduled</span> : null}
                </td>
                <td className={br.source}>{en?.source ? <span title={en.source}>{en.source}</span> : needsSource ? <span className={m.required}>missing</span> : <span className={styles.faint}>habit · none needed</span>}</td>
                <td data-align="right" className="mono">
                  {fmtInt(c.opens)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- editor

function BrainDialog({ editing, cards, canEdit, onClose }: { editing: Editing; cards: AdminBrainCard[]; canEdit: boolean; onClose: () => void }) {
  const card = editing.card;
  const create = useCreateBrainCard();
  const update = useUpdateBrainCard();
  const remove = useDeleteBrainCard();
  const [category, setCategory] = useState<BrainCategory>(card?.category ?? "brain");
  const [text, setText] = useState<ContentState<TextKey>>(() => textState(TEXT_FIELDS, card?.content));
  const [locale, setLocale] = useState<Locale>("en");
  const [image, setImage] = useState<ImageRef | null>(card?.image ?? null);
  const [date, setDate] = useState(card ? (card.date ?? "") : editing.date ?? "");
  const [evergreen, setEvergreen] = useState(card?.evergreen ?? false);
  const [status, setStatus] = useState<BrainStatus>(card?.status ?? "draft");
  const [deleting, setDeleting] = useState(false);
  const save = card ? update : create;

  const en = text.draft.en;
  const problem = !en.title.trim() || !en.text.trim() ? "Write the English title and text." : status === "ready" && category !== "habit" && !en.source.trim() ? SOURCE_RULE : null;
  const clash = date ? cards.find((c) => c.date === date && c.id !== card?.id) : undefined;

  const submit = () => {
    const draft: BrainCardDraft = {
      date: date || null,
      evergreen,
      category,
      content: toLocalized(text.draft, TEXT_FIELDS).content as BrainContent,
      imageId: image?.id ?? null,
      status,
    };
    if (card) update.mutate({ id: card.id, patch: draft }, { onSuccess: onClose });
    else create.mutate(draft, { onSuccess: onClose });
  };

  return (
    <Dialog
      open
      title={card ? (canEdit ? "Edit brain card" : "Brain card") : date ? `New brain card for ${formatShortDate(date)}` : "New brain card"}
      onClose={onClose}
      width={1060}
      footer={
        canEdit ? (
          <>
            <div className={br.footLeft}>
              {card ? (
                <Button variant="ghost" onClick={() => setDeleting(true)} disabled={save.isPending}>
                  Delete…
                </Button>
              ) : null}
              {problem ? (
                <span className={br.footProblem} data-testid="card-problem">
                  {problem}
                </span>
              ) : null}
            </div>
            <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button variant="primary" busy={save.isPending} disabled={problem !== null} onClick={submit} data-testid="card-save">
              {card ? "Save changes" : "Create card"}
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      <div className={br.editor}>
        <fieldset className={br.form} disabled={!canEdit || save.isPending}>
          <Field label="Category" hint={BRAIN_CATEGORY_HINT[category]}>
            <Segmented label="Category" value={category} onChange={setCategory} options={BRAIN_CATEGORIES.map((c) => ({ value: c, label: BRAIN_CATEGORY_LABEL[c] }))} />
          </Field>
          <ContentEditor
            value={text}
            onChange={setText}
            fields={TEXT_SPECS}
            englishRequired={["title", "text"]}
            locale={locale}
            onLocale={setLocale}
            needs={{ source: status === "ready" && category !== "habit" ? "required before it can be ready" : null }}
            note={<ClaimsNote>Every factual card needs a source (only habit cards may go without). Describe how the brain and reasoning work; never promise IQ gains or health benefits.</ClaimsNote>}
          />
          <ImageField image={image} onChange={setImage} recommended={IMAGE} hint="1200×675 (16:9) recommended, an illustration without text" testId="card-image" />
          <div className={br.twoUp}>
            <Field label="Date (UTC calendar day)" hint={clash ? <span className={m.required}>{date} already has “{clash.content.en?.title}”.</span> : "Empty: not scheduled. One card per date."}>
              <span className={br.dateRow}>
                <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="card-date" />
                {date ? (
                  <Button size="sm" variant="ghost" onClick={() => setDate("")}>
                    Clear
                  </Button>
                ) : null}
              </span>
            </Field>
            <SwitchRow title="Evergreen" hint="In the rotation for days with nothing scheduled." checked={evergreen} onChange={setEvergreen} label="Evergreen" testId="card-evergreen" />
          </div>
          <Field label="Status" hint={status === "ready" ? "Ready: players get it on its date or in the rotation." : "Draft: no player sees it."}>
            <Segmented
              label="Status"
              value={status}
              onChange={setStatus}
              options={[
                { value: "draft", label: "Draft" },
                { value: "ready", label: "Ready" },
              ]}
            />
          </Field>
          <InlineError error={save.error} />
        </fieldset>
        <aside className={br.previewSide}>
          <CardPreview content={text} locale={locale} category={category} image={image} />
        </aside>
      </div>
      <ConfirmDialog
        open={deleting}
        title={`Delete “${en.title || "this card"}”?`}
        confirmLabel="Delete card"
        tone="danger"
        busy={remove.isPending}
        error={remove.error}
        onCancel={() => setDeleting(false)}
        onConfirm={() => card && remove.mutate(card.id, { onSuccess: onClose })}
      >
        <p className={styles.prose}>{card?.date ? `${card.date} falls back to the evergreen rotation. ` : ""}Its opens go with it.</p>
      </ConfirmDialog>
    </Dialog>
  );
}

/** The card as the app shows it: on Home (Today), and opened full screen with the “Why” and the source. */
function CardPreview({ content, locale, category, image }: { content: ContentState<TextKey>; locale: Locale; category: BrainCategory; image: ImageRef | null }) {
  const t = resolveEntry(content.draft, locale);
  const rtl = RTL_LOCALES.has(locale) ? "rtl" : undefined;
  return (
    <div className={br.preview} data-testid="card-preview">
      <div className={m.previewHead}>
        <span className="monoLabel">Home card</span>
        {t.fallback.length ? <span className={m.fallbackNote}>English for: {t.fallback.join(", ")}</span> : null}
      </div>
      <div className={br.homeCard} dir={rtl} data-testid="preview-home">
        <span className={br.kicker}>Today's brain card · {BRAIN_CATEGORY_LABEL[category]}</span>
        <strong>{t.title || "Title"}</strong>
        <p>{t.text || "The card's text."}</p>
        {t.more ? <span className={br.why}>Why ›</span> : null}
      </div>
      <span className="monoLabel">Opened</span>
      <div className={m.phone} dir={rtl} data-testid="preview-full">
        <div className={m.phoneBar}>
          <span>‹ Today</span>
          <span>{BRAIN_CATEGORY_LABEL[category]}</span>
        </div>
        {image ? <img src={image.url} alt="" className={br.fullImage} /> : null}
        <h3 className={br.fullTitle}>{t.title || "Title"}</h3>
        <p className={br.fullText}>{t.text || "The card's text."}</p>
        {t.more ? (
          <div className={br.whyBlock}>
            <span className="monoLabel">Why</span>
            {t.more.split(/\n\s*\n/).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        ) : null}
        {t.source ? <p className={br.sourceLine}>Source: {t.source}</p> : null}
      </div>
    </div>
  );
}
